"use client";

import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import Image from 'next/image';
import Link from 'next/link';
import googleLogo from '@/public/google.svg';
import sdeMarketplaceLogo from '@/public/sdemarketplaceLogo2.png';
import { Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  // Define error type
  type ErrorState = {
    emailOrUsername?: string;
    password?: string;
  };

  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<ErrorState>({});
  const [showPassword, setShowPassword] = useState(false);

  //Form validation and submission
  const validate = () => {
    const tempErrors: ErrorState = { emailOrUsername: "", password: "" };
    let isValid = true;

    // Email or Username validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const usernameRegex = /^[a-zA-Z0-9._-]{3,}$/;

    if (!email.trim()) {
      tempErrors.emailOrUsername = "Username or email is required.";
      isValid = false;
    } else if (!emailRegex.test(email) && !usernameRegex.test(email)) {
      tempErrors.emailOrUsername = "Enter a valid email or username.";
      isValid = false;
    }

    // Password validation
    if (!password.trim()) {
      tempErrors.password = "Password is required.";
      isValid = false;
    } else if (password.length < 6) {
      tempErrors.password = "Password must be at least 8 characters.";
      isValid = false;
    }

    setError(tempErrors);
    return isValid;
  };

  const handleLogin = async () => {
    if (!validate()) return;

    const res = await signIn("credentials", {
      email,
      password,
      redirect: false,
    });

    if (res?.error) {
      try {
        const errorData = JSON.parse(res.error);
        if (errorData.code === "EMAIL_NOT_VERIFIED") {
          const userActualEmail = errorData.email;
          await fetch("/api/auth/resend", {
            method: "POST",
            body: JSON.stringify({ email: userActualEmail }),
            headers: { "Content-Type": "application/json" },
          });
          router.push(`/verify?email=${userActualEmail}`);
        }
      } catch {
        if (res.error === "USER_NOT_FOUND") {
          setError({ emailOrUsername: "No account found with this email/username." });
        } else if (res.error === "INVALID_PASSWORD") {
          setError({ password: "Wrong password." });
        } else {
          setError({ emailOrUsername: "An unexpected error occurred." });
        }
      }
    } else if (res?.ok) {
      // On successful login, redirect to the dashboard.
      router.push("/dashboard");
    }
  };

  return (
    <section className="bg-slate-50 flex items-center justify-center min-h-screen py-10 md:py-16 px-4">
      <div className="w-full max-w-lg mx-auto p-6 md:p-12 bg-white rounded-2xl border border-gray-200 shadow-lg">

        {/* 1. Logo */}
        <div className="flex justify-center mb-8 md:mb-10">
          <Link href="/">
            <Image
              src={sdeMarketplaceLogo}
              alt="SDeMARKETPLACE Logo"
              width={220}
              height={50}
              priority
              className="w-44 md:w-56 h-auto"
            />
          </Link>
        </div>

        {/* 2. Form */}
        <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} className="space-y-5 md:space-y-6">

          {/* Email Input */}
          <div>
            <label htmlFor="email" className="block text-sm md:text-base font-semibold text-slate-700 mb-1 md:mb-2">
              Username or Email<span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              placeholder="Enter your username or email"
              className={`w-full px-4 py-2 md:py-3 rounded-lg focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 border text-sm md:text-base
              ${error.emailOrUsername ? "border-red-500" : "border-gray-200"}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              suppressHydrationWarning
            />
            {/* Error Message Email*/}
            {error.emailOrUsername && (
              <p className="text-red-600 text-xs md:text-sm mt-1">{error.emailOrUsername}</p>
            )}
          </div>

          {/* Password Input */}
          <div>
            <div className="flex justify-between items-center mb-1 md:mb-2">
              <label htmlFor="password" className="text-sm md:text-base font-semibold text-slate-700">
                Password<span className="text-red-500">*</span>
              </label>
              <a href="/forget-password" className="text-xs md:text-sm text-indigo-700 hover:underline">
                Forgot password?
              </a>
            </div>
            {/* Add relative container for the icon button */}
            <div className="relative">
              <input
                id="password"
                // Change type based on state
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                className={`w-full px-4 py-2 md:py-3 rounded-lg focus:ring-0 placeholder:text-gray-400 placeholder:text-sm md:placeholder:text-base focus:outline-none text-slate-800 border text-sm md:text-base
                ${error.password ? "border-red-500" : "border-gray-200"}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}

                suppressHydrationWarning
              />
              {/* The icon button */}
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 md:pr-4 text-slate-400 hover:text-indigo-600"
                suppressHydrationWarning
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 md:h-5 md:w-5" />
                ) : (
                  <Eye className="h-4 w-4 md:h-5 md:w-5" />
                )}
              </button>
            </div>
            {/* Error Message Password*/}
            {error.password && (
              <p className="text-red-600 text-xs md:text-sm mt-1">{error.password}</p>
            )}
          </div>

          {/* Sign In Button */}
          <div>
            <button
              type="submit"
              className="w-full bg-[#1e2d4d] text-white font-semibold py-2.5 md:py-3 px-6 rounded-lg transition-colors duration-200 hover:bg-indigo-700 text-sm md:text-base hover:cursor-pointer"
              suppressHydrationWarning
            >
              Sign In
            </button>
          </div>
        </form>

        {/* Google Sign-In */}
        <button
          onClick={() => signIn('google', { callbackUrl: '/dashboard' })}
          className="mt-4 w-full flex items-center justify-center gap-2 md:gap-3 bg-white text-slate-700 font-semibold py-2.5 md:py-3 rounded-lg border border-gray-200 hover:bg-slate-200 transition-colors text-sm md:text-base hover:cursor-pointer"
          suppressHydrationWarning
        >
          <Image
            src={googleLogo}
            alt="Google logo"
            height={20}
            width={20}
            className="h-4 w-4 md:h-5 md:w-5"
          />
          <span>Continue with Google</span>
        </button>

        {/* Sign Up Link */}
        <div className="text-center mt-4">
          <p className="text-xs md:text-sm text-slate-600">
            Don&apos;t have an account?{" "}
            <a href="/signup" className="text-indigo-700 hover:underline font-semibold">
              Sign Up Now!
            </a>
          </p>
        </div>

      </div>
    </section>
  );
}